import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";

import PublicLayout from "@/layouts/PublicLayout";
import StaffLayout from "@/layouts/StaffLayout";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Menu from "@/pages/Menu";
import Events from "@/pages/Events";
import Gallery from "@/pages/Gallery";
import Reservations from "@/pages/Reservations";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import CancelReservation from "@/pages/CancelReservation";

import StaffDashboard from "@/pages/staff/Dashboard";
import StaffReservations from "@/pages/staff/Reservations";
import SlotBlocks from "@/pages/staff/SlotBlocks";
import VendorPerformance from "@/pages/staff/VendorPerformance";
import StaffGallery from "@/pages/staff/Gallery";
import StaffEvents from "@/pages/staff/Events";
import KitchenBrief from "@/pages/staff/KitchenBrief";
import StaffPartyMenu from "@/pages/staff/PartyMenu";
import StaffUsers from "@/pages/staff/Users";
import BookingConfirmation from "@/pages/staff/BookingConfirmation";
import StaffMenu from "@/pages/staff/Menu";
import StaffChangePassword from "@/pages/staff/ChangePassword";
import StaffReminders from "@/pages/staff/Reminders";
import StaffCalendar from "@/pages/staff/Calendar";
import StaffEmployees from "@/pages/staff/Employees";
import EmployeeDetail from "@/pages/staff/EmployeeDetail";
import StaffKOTs from "@/pages/staff/KOTs";
import WhatsAppLog from "@/pages/staff/WhatsAppLog";
import Procurement from "@/pages/staff/Procurement";
import ProcurementIntelligence from "@/pages/staff/ProcurementIntelligence";
import ProductOut from "@/pages/staff/ProductOut";
import StaffMenuInternal from "@/pages/staff/StaffMenuInternal";
import StaffMeals from "@/pages/staff/StaffMeals";
import Inventory from "@/pages/staff/Inventory";
import Recipes from "@/pages/staff/Recipes";
import Backups from "@/pages/staff/Backups";

function NotFound() {
  return (
    <div data-testid="not-found" className="pt-40 pb-24 text-center">
      <div className="eyebrow">404</div>
      <h1 className="mt-3 display text-5xl">We can't find that page.</h1>
      <a href="/" className="btn-brass mt-8 inline-flex">Back to home</a>
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="bottom-right" richColors />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/menu" element={<Menu />} />
            <Route path="/events" element={<Events />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/reservations" element={<Reservations />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/r/cancel/:token" element={<CancelReservation />} />
          </Route>

          {/* Standalone staff portal — no public header/footer chrome. */}
          <Route path="/login" element={<Login />} />

          <Route
            path="/staff"
            element={
              <ProtectedRoute>
                <StaffLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<StaffDashboard />} />
            <Route path="calendar" element={<StaffCalendar />} />
            <Route path="reservations" element={<StaffReservations />} />
            <Route path="slot-blocks" element={<SlotBlocks />} />
            <Route path="vendor-performance" element={<VendorPerformance />} />
            <Route path="reminders" element={<StaffReminders />} />
            <Route path="menu" element={<StaffMenu />} />
            <Route path="gallery" element={<StaffGallery />} />
            <Route path="events" element={<StaffEvents />} />
            <Route path="party-menu" element={<StaffPartyMenu />} />
            <Route path="kitchen-brief" element={<KitchenBrief />} />
            <Route path="kots" element={<StaffKOTs />} />
            <Route path="procurement" element={<Procurement />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="procurement-intelligence" element={<ProcurementIntelligence />} />
            <Route path="product-out" element={<ProductOut />} />
            <Route path="recipes" element={<Recipes />} />
            <Route path="backups" element={<Backups />} />
            <Route path="staff-menu" element={<StaffMenuInternal />} />
            <Route path="staff-meals" element={<StaffMeals />} />
            <Route path="whatsapp-log" element={<WhatsAppLog />} />
            <Route path="users" element={<StaffUsers />} />
            <Route path="employees" element={<StaffEmployees />} />
            <Route path="employees/:id" element={<EmployeeDetail />} />
            <Route path="change-password" element={<StaffChangePassword />} />
          </Route>

          {/* Printable booking confirmation — protected but rendered outside StaffLayout for clean printing */}
          <Route
            path="/staff/reservations/:id/print"
            element={
              <ProtectedRoute>
                <BookingConfirmation />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}
